/**
 * Location Coordinate Extraction Utility
 * 
 * Extracts latitude and longitude from Location objects with proper fallback chain.
 * Used by both List and Itinerary place addition functionality.
 * 
 * Priority order:
 * 1. location.latitude / location.longitude (direct properties)
 * 2. location.geoposition.coordinates (GeoJSON: [lng, lat])
 * 3. location.google_places_data.location (Google Places format)
 */

import Location from '@/models/location';

/**
 * Location object structure for coordinate extraction
 * Supports multiple location data formats
 */
interface LocationWithCoordinates {
  latitude?: number;
  longitude?: number;
  geoposition?: {
    coordinates?: [number, number];
    type?: string;
  };
  google_places_data?: {
    location?: {
      latitude?: number;
      longitude?: number;
    };
  };
}

/**
 * Extracted coordinates result
 */
export interface ExtractedCoordinates {
  lat: number;
  lng: number;
}

/**
 * Extracts coordinates from a Location object with proper fallback chain
 * 
 * Priority order:
 * 1. Direct latitude/longitude properties
 * 2. GeoJSON geoposition coordinates (format: [longitude, latitude])
 * 3. Google Places data location
 * 
 * @param location - Location object with various coordinate formats
 * @returns Object with lat and lng, or { lat: 0, lng: 0 } if not found
 */
export function extractLocationCoordinates(
  location: Location | LocationWithCoordinates
): ExtractedCoordinates {
  // Priority 1: Direct latitude/longitude properties
  if (
    typeof location.latitude === 'number' &&
    typeof location.longitude === 'number' &&
    !isNaN(location.latitude) &&
    !isNaN(location.longitude)
  ) {
    return {
      lat: location.latitude,
      lng: location.longitude,
    };
  }

  // Priority 2: GeoJSON geoposition (format: [longitude, latitude])
  const geopositionCoordinates = location.geoposition?.coordinates;
  if (
    Array.isArray(geopositionCoordinates) &&
    geopositionCoordinates.length === 2 &&
    typeof geopositionCoordinates[0] === 'number' &&
    typeof geopositionCoordinates[1] === 'number' &&
    !isNaN(geopositionCoordinates[0]) &&
    !isNaN(geopositionCoordinates[1])
  ) {
    // GeoJSON format: [longitude, latitude]
    return {
      lat: geopositionCoordinates[1],
      lng: geopositionCoordinates[0],
    };
  }

  // Priority 3: Google Places data location (Record shape from Location model)
  const googlePlacesLocation = location.google_places_data?.location as
    | Record<string, unknown>
    | undefined;
  if (googlePlacesLocation && typeof googlePlacesLocation === 'object') {
    const googleLat = googlePlacesLocation.latitude;
    const googleLng = googlePlacesLocation.longitude;

    if (
      typeof googleLat === 'number' &&
      typeof googleLng === 'number' &&
      !isNaN(googleLat) &&
      !isNaN(googleLng)
    ) {
      return {
        lat: googleLat,
        lng: googleLng,
      };
    }
  }

  // Fallback: return 0,0 if no valid coordinates found
  return { lat: 0, lng: 0 };
}

